import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Button,
  InputLabel,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { PartnerArrowIcon } from "../../res/NewWebIcons";

const UserServiceManagement = () => {
  const [userType, setUserType] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [providerpayinData, setPayinProviderData] = useState([]);
  const [providerpayoutData, setPayoutProviderData] = useState([]);
  const [selectedPayinProvider, setSelectedPayinProvider] = useState("");
  const [selectedPayoutProvider, setSelectedPayoutProvider] = useState("");
  const [service, setService] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleUserTypeChange = async (event) => {
    const selectedType = event.target.value;
    setUserType(selectedType);
    setSelectedUserId(null);

    // Fetch users based on selected user type
    const jwtToken = sessionStorage.getItem("jwtToken");
    try {
      setLoading(true);
      const response = await axios.get(
        `https://api.ipaisa.co.in/myapp/admin/getAllPartner`,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
          params: {
            userrole: selectedType,
          },
        }
      );

      if (response.status === 200) {
        setUsers(response.data); // Assuming the response contains the list of users
      } else {
        console.error("Failed to fetch partners");
      }
    } catch (error) {
      console.error("Error fetching partners:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelection = async (userId) => {
    setSelectedUserId(userId);
    const jwtToken = sessionStorage.getItem("jwtToken");

    try {
      const response = await axios.get(
        `https://api.ipaisa.co.in/myapp/admin/checkUserPayinPayoutService?userId=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );

      if (response.data.statusCodeValue === 200) {
        setUserData(response.data.body); // Store response data
      } else {
        console.error("Failed to fetch pay-in / pay-out service data");
      }
    } catch (error) {
      console.error("Error fetching pay-in / pay-out service data:", error);
    }
  };

  const fetchPayinProviderData = async () => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    try {
      const response = await axios.get(
        "https://api.ipaisa.co.in/myapp/admin/get-all-gatway-provider",
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );

      console.log("Payin Provider Response:", response.data);

      if (response.data.status === 200 && response.data.users) {
        setPayinProviderData(response.data.users); // Store provider data
      } else {
        console.error(
          "Unexpected response format for PayIn providers:",
          response.data
        );
      }
    } catch (error) {
      console.error("Error fetching PayIn provider data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayoutProviderData = async () => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    try {
      const response = await axios.get(
        "https://api.ipaisa.co.in/myapp/admin/get-all-payout-provider",
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );

      console.log("Payout Provider Response:", response.data);

      if (response.data.status === 200 && response.data.users) {
        setPayoutProviderData(response.data.users); // Store provider data
      } else {
        console.error(
          "Unexpected response format for Payout providers:",
          response.data
        );
      }
    } catch (error) {
      console.error("Error fetching PayOut provider data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderSelect = (providerName, type) => {
    if (type === "payin") {
      setSelectedPayinProvider(providerName);
    } else {
      setSelectedPayoutProvider(providerName);
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedUserId) {
      toast.error("Please select a user first");
      return;
    }

    setIsSaving(true);
    const jwtToken = sessionStorage.getItem("jwtToken");

    try {
      const url = `https://api.ipaisa.co.in/myapp/admin/updateUserPayinPayoutService`;
      const response = await axios.put(
        url,
        {},
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
          params: {
            userId: selectedUserId,
            ...(selectedPayinProvider && { payinService: selectedPayinProvider }),
            ...(selectedPayoutProvider && { payoutService: selectedPayoutProvider }),
          },
        }
      );

      if (response.data.statusCodeValue === 200) {
        toast.success("Services updated successfully");
        // Refresh user data
        handleUserSelection(selectedUserId);
      } else {
        toast.error("Failed to update services");
      }
    } catch (error) {
      console.error("Error updating services:", error);
      toast.error("Error updating services");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
  };

  const handleClear = () => {
    setSearchQuery("");
  };

  const handleCancel = () => {
    // Reset all states to initial values
    setUserType("");
    setUsers([]);
    setSelectedUserId(null);
    setUserData(null);
    setSearchQuery("");
    setSelectedPayinProvider("");
    setSelectedPayoutProvider("");
    setService("");
  };

  useEffect(() => {
    fetchPayinProviderData();
    fetchPayoutProviderData();
  }, []);

  return (
    <Box style={{ padding: "2rem", marginTop: "calc(3% + 56px)" }}>
      {/* Breadcrumb */}
      <Grid container alignItems="center" spacing={1} style={{ marginBottom: "20px" }}>
        <Grid item>
          <Typography color="textSecondary" style={{ fontSize: "14px" }}>
            Service Handler
          </Typography>
        </Grid>
        <Grid item>
          <PartnerArrowIcon />
        </Grid>
        <Grid item>
          <Typography color="textSecondary" style={{ fontSize: "14px" }}>
            User Service Management
          </Typography>
        </Grid>
      </Grid>

      {/* Main Title */}
      <Typography
        variant="h5"
        style={{
          fontWeight: "bold",
          color: "#343C6A",
          marginBottom: "24px"
        }}
      >
        User Service Management
      </Typography>

      <Paper style={{ padding: "24px", borderRadius: "12px", boxShadow: "none", border: "1px solid #e0e0e0" }}>
        <Grid container spacing={3}>
          {/* User Selection Section */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>User Type</InputLabel>
              <Select
                label="User Type"
                value={userType}
                onChange={handleUserTypeChange}
              >
                <MenuItem value="AREADISTRIBUTOR">Area Distributor</MenuItem>
                <MenuItem value="RETAILER">Retailer</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedUserId || ""}
                onChange={(e) => handleUserSelection(e.target.value)}
                label="Select User"
                disabled={!userType}
              >
                {users
                  .filter((user) => user.Name.toLowerCase().includes(searchQuery))
                  .map((user) => (
                    <MenuItem key={user.userId} value={user.userId}>
                      {user.userId} - {user.Name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Current Services Status */}
          {userData && (
            <Grid item xs={12}>
              <Typography variant="h6" style={{ marginBottom: "16px", color: "#343C6A" }}>
                Current Services Status
              </Typography>
              <TableContainer component={Paper} style={{ boxShadow: "none", border: "1px solid #e0e0e0" }}>
                <Table>
                  <TableHead style={{ backgroundColor: "#027BFE" }}>
                    <TableRow>
                      <TableCell style={{ color: "white" }}>Service</TableCell>
                      <TableCell style={{ color: "white" }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Active PayIn</TableCell>
                      <TableCell>{userData.active_payin || "Not Set"}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Active Payout</TableCell>
                      <TableCell>{userData.active_payout || "Not Set"}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          {/* Service Provider Selection */}
          {selectedUserId && (
            <>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>PayIn Provider</InputLabel>
                  <Select
                    value={selectedPayinProvider}
                    onChange={(e) => handleProviderSelect(e.target.value, "payin")}
                    label="PayIn Provider"
                  >
                    {providerpayinData.map((provider) => (
                      <MenuItem key={provider.providerName} value={provider.providerName}>
                        {provider.providerName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Payout Provider</InputLabel>
                  <Select
                    value={selectedPayoutProvider}
                    onChange={(e) => handleProviderSelect(e.target.value, "payout")}
                    label="Payout Provider"
                  >
                    {providerpayoutData.map((provider) => (
                      <MenuItem key={provider.providerName} value={provider.providerName}>
                        {provider.providerName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Save and Cancel Buttons */}
              <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px", gap: "16px" }}>
                <Button
                  onClick={handleCancel}
                  sx={{
                    border: "1.5px solid #FF0000",
                    color: "#FF0000",
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: "8px 24px",
                    "&:hover": {
                      backgroundColor: "rgba(255, 0, 0, 0.04)"
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveChanges}
                  disabled={isSaving || (!selectedPayinProvider && !selectedPayoutProvider)}
                  sx={{
                    backgroundColor: "#027BFE",
                    color: "white",
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: "8px 24px",
                    "&:hover": {
                      backgroundColor: "#0261CB"
                    }
                  }}
                >
                  {isSaving ? (
                    <>
                      <CircularProgress size={20} color="inherit" style={{ marginRight: 8 }} />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default UserServiceManagement;
